import { Dispatch, SetStateAction, FC, PropsWithChildren, ReactNode, useId } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Checklist } from '@eneco/components/ui/Checklist/Checklist';
import { Price } from '@eneco/flows/components';
import { Box, Stack, Text, Badge, RadioCard, Expandable, Divider, Heading, Stretch } from '@eneco/sparky';

import { Price as PriceType } from '../../../shared/types';

interface Props {
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  isCashbackAvailable: boolean;
  isTagAvailable: boolean;

  id: string;
  title?: string;
  subtitle?: string;
  ribbon?: ReactNode;

  priceMonthly?: PriceType;
  priceYearly?: PriceType;

  priceDetailsTriggerLabel?: string;
  loyaltyBonusLabel?: string;

  tag?: string;
  usps?: string[];
}

export const ProductCard: FC<PropsWithChildren<Props>> = ({
  id: productId,
  title,
  subtitle,
  ribbon,
  tag,
  priceMonthly,
  priceYearly,
  priceDetailsTriggerLabel,
  usps,
  loyaltyBonusLabel,

  isOpen,
  setOpen,

  isCashbackAvailable,
  isTagAvailable,

  children,
}) => {
  const id = useId();

  return (
    <Stretch>
      <RadioCard value={productId} ariaLabelledby={`${id}-label ${id}-price`}>
        <Box padding="4" backgroundColor="backgroundCMSVarTwo" borderTopLeftRadius="m" borderTopRightRadius="m">
          <Text color="textInverted">
            <Stack direction="row" gap="3" alignY="center">
              <RadioCard.Indicator />
              <Stack>
                <Text id={`${id}-label`} weight="bold" size="BodyXL">
                  {title}
                </Text>
                <Text size="BodyS">{subtitle}</Text>
              </Stack>
            </Stack>
          </Text>
        </Box>

        <Box padding="6">
          <Stack gap="3">
            {priceMonthly && (
              <Stack.Item>
                <Stack direction="row" alignX="justify" alignY="justify" gap="4">
                  <Stack.Item>
                    <Text size="BodyXS">{priceMonthly.period}</Text>
                  </Stack.Item>
                  <Stack.Item shrink={false}>
                    <Heading as="h2" size={{ initial: 'XS', sm: 'M' }}>
                      <Price id={`${id}-price-monthly`}>{priceMonthly.value}</Price>
                    </Heading>
                  </Stack.Item>
                </Stack>

                {tag ? (
                  <Text align="right">
                    <Badge color="alpha">{tag}</Badge>
                  </Text>
                ) : (
                  // whenever the "isTagAvailable" to guaranty correct
                  // alignment tag filler will be used
                  isTagAvailable && <Box paddingBottom="6"> </Box>
                )}
              </Stack.Item>
            )}

            {priceYearly && (
              <>
                <Stack direction="row" alignX="justify" alignY="start" gap="12">
                  <Text size="BodyXS" weight="bold">
                    {priceYearly.period}
                  </Text>

                  <Stack direction={{ initial: 'column', md: 'row' }} alignX="end" gap={{ initial: '0', md: '1' }}>
                    <Stack.Item>
                      <Text size="BodyS" weight="bold">
                        {priceYearly.previousValue ? (
                          <s>{priceYearly.previousValue}</s>
                        ) : (
                          <Price id={`${id}-price-yearly`}>{priceYearly.value}</Price>
                        )}
                      </Text>
                    </Stack.Item>
                  </Stack>
                </Stack>

                {priceYearly.previousValue && (
                  <Stack direction="row" alignX="justify" alignY="start" gap="12">
                    <Text size="BodyXS" weight="regular">
                      {loyaltyBonusLabel}
                    </Text>
                    <Text size="BodyS" weight="regular">
                      <Price id={`${id}-price`} emphasis="low">
                        {priceYearly.value}
                      </Price>
                    </Text>
                  </Stack>
                )}
              </>
            )}
          </Stack>
        </Box>

        {/* whenever the "isCashbackAvailable" to guaranty correct alignment ribbon filler will be used */}
        {ribbon || (isCashbackAvailable && <Box padding="8"> </Box>)}

        {priceDetailsTriggerLabel && (
          <>
            <Expandable isOpen={isOpen} setOpen={setOpen}>
              <Box paddingLeft="6" paddingRight="4">
                <Expandable.Trigger isTransparent aria-labelledby={`${id}-price-details`}>
                  <Box paddingY="3">
                    <Text weight="bold" id={`${id}-price-details`}>
                      {priceDetailsTriggerLabel}
                    </Text>
                  </Box>
                </Expandable.Trigger>
              </Box>

              <Expandable.Content>
                <Box>{children}</Box>
              </Expandable.Content>
            </Expandable>

            <Divider />
          </>
        )}

        {usps && (
          <Box padding="6">
            <Checklist>
              {usps.map(usp => (
                <Checklist.Item key={usp} gap="2">
                  <Stack>
                    <RichText html={usp} />
                  </Stack>
                </Checklist.Item>
              ))}
            </Checklist>
          </Box>
        )}
      </RadioCard>
    </Stretch>
  );
};
